import * as $ from 'jquery';
import * as _ from 'underscore';

import { IGlob } from './../Glob';
import BaseStore from '../../../../_common/platform/ts/BaseStore';
import { IValuesCollection } from '../../../../_common/platform/ts/appTheme';
import * as Utilities from '../../../../_common/platform/ts/Utilities';

import { useEffect, useState } from 'react';

import { NullHandling, nullHandlingBackend, nullHandlingBehaviour } from '../../../../_common/platform/ts/nullHandling';

import {
    EntityModelsActionsConstants as ActionConstants,
    EntityModelsActions,
    EntityModelsDispatcher,
    checkAction,
    EntityModelsActionInterfaces as I
} from '../actions/EntityModelsActions';

import {
    IIntervals,
    ILoadDataResult,
    IAttributeReference,
    IDataParameters
} from '../../../../_common/platform/ts/AqlUtility';

import { IAQLFilter, IAttributeName } from '../../../../_common/platform/tsx/filtering/Interfaces';

import {
    TIME_PART_ELEMENTS,
    getElementNameByType,
    ATTRIBUTE_TYPES
} from '../../../../_common/platform/ts/ConfigurationConstants';

import { IDisplayValue, ITreeItemGeneral } from '../../../../_common/platform/ts/Interfaces';
import { IAQLOptions, ITreeNode, ITreeNodesCollection, ITreeValues } from '../Interfaces';

export const ATTRIBUTE_SEPARATOR = '.';
export const DEFAULT_DATASOURCE_ID = 1;

export type AttributeValueSortingType = 'asc' | 'dsc' | 'default';

export interface IEntityDataEx_MultiColLinkType {
    SourceSQLCol: string;
    DestSQLCol: string;
}

export interface IEntityDataEx_Suggestions {
    AQLTypeList?: string[];
    SuggestAQLType?: string;
    SuggestAttribName?: string;
    SuggestReferEntity?: string;
}

export interface IEntityDataEx_Attribute {
    AQLType: string;
    AtribName: string;
    Description: string;
    Label: string; // should be '' if empty
    MultiColLink?: IEntityDataEx_MultiColLinkType[];
    Nullable?: boolean;
    PeriodType?: string;
    ReferEntity: string;

    // either One or Another:
    Expression?: any;
    SQLCol: string; //(Note can be empty for Entity references),  /* non editable */

    SQLLength?: number; //(Note can be empty for Entity references) /* non editable */
    SQLType?: string; //(Note can be empty for Entity references), /* non editable */
    Include: boolean; // True if col should be included
    Suggestions?: IEntityDataEx_Suggestions;
    input: boolean; //is input (for boolean f.)
    order?: number;
}

export interface IEntityDataEx {
    Attributes: IEntityDataEx_Attribute[];
    DefaultAttribute: string;
    EntityType?: number; //0-table, 1-view, 2-func
    IsFunc: false;
    IsScalarFunc: boolean;
    IsVirtual?: false; // not implemented yet ???
    Label: string; // should be '' if empty
    Name: string;
    SQLColForSortOnValues: string;
    SQLIdCol: string;
    SQLTable: string;
    allowToAnalyzeView: boolean;
    Description: string;
}

export interface IRelation {
    attribute: string;
    entity: string;
}

export interface IDataLoadEntity {
    name: string;
    refer: string;
    type: string;
    originalType?: string;

    DefaultAttribute: string;
    IsFunc: boolean;
    IsScalarFunc: boolean;
    IsVirtual: boolean;
    IsEditableVirtualEntity: boolean;
    IsView: boolean;
    isBackwardReference?: boolean;
    isUnique: boolean;
    is_enum: boolean;
    isSystemVersioned: boolean;
    isSysDeletedExists: boolean;
    isSysUserExists: boolean;
    keyAttribute: string;
    label: string;
    keyIsAutoGen?: boolean;
    allowToAnalyzeView: boolean;
    historyEntity?: string;
    attributes: IDataLoadEntityAttribute[];

    // Calculated fields:
    relations?: IRelation[];
}

export interface IDataLoadEntityAttribute extends IDataLoadEntity {
    autoGen?: boolean;
    Nullable?: boolean;
    length?: number;
    order?: number;
    input?: boolean;
    Expression?: {
        func?: {
            name?: string;
            params?: any[];
        };
    };
}

export interface ITreeItem extends ITreeItemGeneral {
    isKeyAttribute?: boolean;
    isDefaultAttribute?: boolean;
    parentEntity?: ITreeItem; // Reference for an Attribute to its Entity.
    original?: IDataLoadEntityAttribute; // It can be IDataLoadEntity or IDataLoadEntityAttribute;
    extendedProperties?: IEntityDataEx;
    children?: ITreeItem[];
}

export enum DataloadEventTypes {
    MODEL,
    MODEL_LEAF,
    ENTITY_VALUES,
    TREE_ENTITY_VALUES,
    DATASOURCES,
    CACHING,
    ENTITY_EXTENDED_DATA
}

export type GetValuesResult = JQuery.Deferred<IGetValuesResult>;

export interface IGetValuesResult {
    displayValues?: IDisplayValue[];
}

export interface IEntitites {
    [dataSourceID: number]: IDataLoadEntity[];
}

export interface IEntityHash {
    [entityName: string]: IDataLoadEntity;
}

export interface IEntityValuesHash {
    [value: string]: {
        type?: string;
        name?: string; // It can be any string field defined by 'displayKey'.
    };
}

export interface IEntityValueCollection {
    values: string[];
    displayValues: IDisplayValue[];
}

export interface ITreeItemsHash {
    [id: string]: ITreeItem;
}

export interface IDatasource {
    dataSourceID: number;
    name: string;
}

export interface IExpression {
    rettype: string;
    syntaxTree: any;
    text?: string;
}

export interface IEntityValuesTree extends ITreeValues {
    parentAttribute?: string;
    rootNodeID?: string;
}

export interface IEntityValuesTreeCollection {
    [attribute: string]: IEntityValuesTree;
}

export interface IAttributeQueryData extends IAttributeName, IAQLOptions {
    loadKeyAttribute?: boolean;
    keyAttribute?: IAttributeName;
    limit?: number;
}

export interface IEntitiesTree {
    [dataSourceID: number]: ITreeItem[];
}

export interface IEntityModelsStoreState {
    dataSources: IDatasource[];
    entities: IEntitites;

    entitiesTree: IEntitiesTree;
    entitiesHash: { [dataSourceID: number]: ITreeItemsHash };

    loadingDataSources: boolean;
    loadingDataSourcesDeffered?: JQueryDeferred<any>;
    dataSourcesAreLoaded: boolean;

    allEntitesAreLoaded: boolean;
    loadingAllModelsDeffered?: JQueryDeferred<IEntitites>;

    loadingModelPromises?: {
        [dataSourceID: number]: JQueryPromise<any>;
    };
}

const MAX_TREE_DEPTH = 1;
const SELECTED_DATASOURCE_ID_KEY = 'SelectedDataSourceID';
const DEFAULT_WORKSPACE_ID = -1;
const DATA_LOADED_EVENT = 'data_loaded';
const MODELS_LOADED_EVENT = 'models_loaded';
const CACHE_CLEARED_EVENT = 'cache_cleared';

export class EntityModelsStore extends BaseStore<
    IEntityModelsStoreState,
    EntityModelsActions,
    EntityModelsDispatcher,
    IGlob
> {
    public static _name = 'entity-models-store';
    public _state: IEntityModelsStoreState = EntityModelsStore.getInitialState();

    constructor(glob: IGlob, dispatcher: EntityModelsDispatcher, actions: EntityModelsActions) {
        super(glob, dispatcher, actions, EntityModelsStore._name, true);
    }

    private static getInitialState(): IEntityModelsStoreState {
        return {
            loadingDataSources: false,
            dataSourcesAreLoaded: false,

            dataSources: [],

            allEntitesAreLoaded: false,
            loadingModelPromises: {},

            entities: {},

            entitiesTree: {},
            entitiesHash: {}
        };
    }

    private cleanCache() {
        this.cleanDataCache();
    }

    public loadDataSources(reload?: boolean): JQueryDeferred<any> {
        const result = $.Deferred<any>();

        if (this._state.loadingDataSources) {
            result.resolve(this._state.dataSources);
            return result;
        }

        const resolveExisting = reload !== true && this._state.dataSources.length > 0;

        if (resolveExisting) {
            result.resolve(this._state.dataSources);
            return result;
        }

        this._state.loadingDataSources = true;
        this._state.loadingDataSourcesDeffered = result;

        this.glob.AQLQueries.callMethod<any>('dataSource_GetAllNames')
            .done((resp) => {
                if (resp.result !== null && resp.result.length > 0) {
                    //VR WorkItem [77396]: Data Source list should be sorted
                    this._state.dataSources = _.sortBy(resp.result, 'name') as IDatasource[];
                }

                this._state.dataSourcesAreLoaded = true;
                result.resolve();
            })
            .fail(() => {
                result.reject();
            })
            .always(() => {
                this._state.loadingDataSources = false;
                delete this._state.loadingDataSourcesDeffered;

                this.actions.loadDataSourcesFinish();
            });

        return result;
    }

    private getAllEntityModels(): JQueryPromise<IEntitites> {
        const result = $.Deferred<IEntitites>();

        this._state.allEntitesAreLoaded = false;
        this._state.loadingAllModelsDeffered = result;

        this.glob.AQLQueries.callMethod<any>('attrQuery_GetEntityModels', {})
            .done(
                (response: {
                    result: {
                        dataSourceID: number;
                        name: string;
                        entitymodel: {
                            entities: IDataLoadEntity[];
                        };
                    }[];
                }) => {
                    // Deleting it in always() here may be too late, because another always() may call ModelFinish event first.
                    delete this._state.loadingAllModelsDeffered;

                    this._state.entities = {};
                    this._state.entitiesTree = {};

                    _.each(response.result, (model) => {
                        this.fillModel(model.dataSourceID, model.entitymodel.entities);
                    });

                    this._state.allEntitesAreLoaded = true;

                    result.resolve(this._state.entities);
                }
            )
            .fail(() => {
                // Deleting it in always() here may be too late, because another always() may call ModelFinish event first.
                delete this._state.loadingAllModelsDeffered;
                result.reject();
            });

        return result.promise();
    }

    private waitForLoadingModelPromises(): JQueryPromise<IEntitites> {
        const result = $.Deferred<IEntitites>();
        const promises = _.values(this._state.loadingModelPromises);

        $.when(...promises)
            .done(() => {
                result.resolve(this._state.entities);
            })
            .fail(() => {
                result.reject();
            });

        return result.promise();
    }

    private static setAutoKeyAttribute(entities: IDataLoadEntity[]): IDataLoadEntity[] {
        return entities?.map((entity) => {
            if (entity.keyIsAutoGen !== true) {
                return entity;
            }

            const find = entity.attributes?.find((attribute) => {
                return attribute.name === entity.keyAttribute;
            });

            if (find != null) {
                find.autoGen = true;
            }

            return entity;
        });
    }

    private static createEntitiesHash(entities: IDataLoadEntity[]): IEntityHash {
        return _.reduce(
            entities,
            (hash, entity: IDataLoadEntity) => {
                hash[entity.name] = entity;
                entity.refer = entity.name;

                return hash;
            },
            {}
        );
    }

    private static createTreeItem(
        hashElement: IDataLoadEntity,
        entity: IDataLoadEntity,
        prefix: string[],
        keyAttribute: string,
        defaultAttribute: string,
        parentEntity: ITreeItem
    ) {
        if (entity.type == null) {
            if (prefix.length === 0) {
                entity.type = ATTRIBUTE_TYPES.ENUMERATION;
            } else {
                entity.type = 'none';
            }
        }

        const name = entity.name;
        const prefix_next = prefix.concat(name);
        const id = prefix_next.join(ATTRIBUTE_SEPARATOR);
        const expandable = hashElement != null && hashElement.attributes.length > 0;

        const obj: ITreeItem = {
            id: id,
            name: name,
            original: entity,
            children: [],
            expandable: expandable
        };

        if (name === keyAttribute) {
            obj.isKeyAttribute = true;
        }

        if (name === defaultAttribute) {
            obj.isDefaultAttribute = true;
        }

        if (parentEntity != null) {
            obj.parentEntity = parentEntity;
        }

        return obj;
    }

    private static sortEntities<T extends IDataLoadEntity>(entities: T[]) {
        const result: T[] = [];
        const backwardReferences: T[] = [];
        const specialEntites: T[] = [];

        Utilities.sortByName(entities).forEach((entity) => {
            if (entity.isBackwardReference) {
                backwardReferences.push(entity);
            } else if (entity.name.length > 0 && entity.name[0] === '#') {
                specialEntites.push(entity);
            } else {
                result.push(entity);
            }
        });

        if (backwardReferences.length > 0) {
            result.push(...backwardReferences);
        }

        if (specialEntites.length > 0) {
            result.push(...specialEntites);
        }

        return result;
    }

    private static getEntitiesTreeArrRec(
        level: number,
        parentNodes: ITreeItem[],
        entititesHash: IEntityHash,
        entities: IDataLoadEntity[],
        prefix: string[],
        attrsHash: ITreeItemsHash,
        keyAttribute?: string,
        defaultAttribute?: string,
        parentEntity?: ITreeItem
    ) {
        if (level > MAX_TREE_DEPTH) {
            return parentNodes;
        }

        const newLevel = level + 1;

        EntityModelsStore.sortEntities(entities).forEach((entity) => {
            const entityHash = entity.refer != null ? entititesHash[entity.refer] : null;
            const item = EntityModelsStore.createTreeItem(
                entityHash,
                entity,
                prefix,
                keyAttribute,
                defaultAttribute,
                parentEntity
            );

            attrsHash[item.id] = item;
            parentNodes.push(item);

            if (entityHash != null && entity.type === ATTRIBUTE_TYPES.ENUMERATION) {
                const elemPrefix = prefix.concat(entity.name);

                EntityModelsStore.getEntitiesTreeArrRec(
                    newLevel,
                    item.children as ITreeItem[],
                    entititesHash,
                    entityHash.attributes,
                    elemPrefix,
                    attrsHash,
                    entityHash.keyAttribute,
                    entityHash.DefaultAttribute,
                    item
                );
            }
        });

        return parentNodes;
    }

    private static setEntityRelations(hash: ITreeItemsHash, entities: IDataLoadEntity[]) {
        _.each(entities, (entity) => {
            _.each(entity.attributes, (attribute) => {
                if (attribute.refer == null) {
                    return;
                }

                const target = hash[attribute.refer];

                if (target == null) {
                    return;
                }

                const original = target.original;

                if (original == null) {
                    return;
                }

                const rels = original.relations || [];

                rels.push({
                    entity: entity.name,
                    attribute: attribute.name
                });

                original.relations = rels;
            });
        });
    }

    private setServicePropertiesToEntity(entity: IDataLoadEntity) {
        if (entity.attributes != null) {
            entity.attributes.forEach((attribute) => {
                this.setServiceProperties(attribute);
            });
        }
    }

    private setServiceProperties(attribute: IDataLoadEntityAttribute) {
        attribute.isBackwardReference = attribute.name != null && attribute.name.indexOf('^') === 0;
        this.setServicePropertiesToEntity(attribute);
    }

    private fillModel(dataSourceID: number, entities: IDataLoadEntity[]) {
        entities = EntityModelsStore.sortEntities(entities);
        entities = EntityModelsStore.setAutoKeyAttribute(entities);

        entities.forEach((entity) => {
            this.setServicePropertiesToEntity(entity);
            entity.attributes = EntityModelsStore.sortEntities(entity.attributes);
        });

        setTypeForAttributes(entities);

        const entititesHash = EntityModelsStore.createEntitiesHash(entities);
        const attrsHash: ITreeItemsHash = {};
        const tree = EntityModelsStore.getEntitiesTreeArrRec(0, [], entititesHash, entities, [], attrsHash);

        EntityModelsStore.setEntityRelations(attrsHash, entities);

        entities.forEach((entity) => {
            entity.relations = Utilities.sortBy(entity.relations, 'displayValue');
        });

        this._state.entities[dataSourceID] = entities;
        this._state.entitiesTree[dataSourceID] = tree;
        this._state.entitiesHash[dataSourceID] = attrsHash;
    }

    private getModels(dataSources: number[], reload: boolean): JQueryPromise<IEntitites> {
        let loadAllModels = true;

        if (dataSources != null && dataSources.length > 0) {
            const modelsToLoad = [];

            _.each(dataSources, (dataSourceID) => {
                //'dataSources' might be an array with null values. If true then load all.
                if (dataSourceID != null) {
                    loadAllModels = false;

                    if (
                        this._state.loadingModelPromises[dataSourceID] == null && //Is not loading already.
                        (reload === true || this._state.entities[dataSourceID] == null)
                    ) {
                        modelsToLoad.push(dataSourceID);
                    }
                }
            });

            if (modelsToLoad.length > 0) {
                _.each(modelsToLoad, (dataSourceID) => {
                    this._state.loadingModelPromises[dataSourceID] = this.glob.AQLQueries.callMethod<any>(
                        'attrQuery_GetEntityModel',
                        {
                            dataSourceID: dataSourceID
                        }
                    )
                        .fail(() => {
                            if (this._state.entities[dataSourceID] == null) {
                                //Initialize collections for not loaded failed models.
                                this._state.entities[dataSourceID] = [];
                                this._state.entitiesTree[dataSourceID] = [];
                                this._state.entitiesHash[dataSourceID] = {};
                            }
                        })
                        .done(
                            (response: {
                                result: {
                                    entities: IDataLoadEntity[];
                                };
                            }) => {
                                this.fillModel(dataSourceID, response.result.entities);
                            }
                        )
                        .always(() => {
                            delete this._state.loadingModelPromises[dataSourceID];
                        });
                });

                this.waitForLoadingModelPromises().always(() => {
                    this.actions.loadModelFinish(modelsToLoad);
                });
            }
        }

        if (loadAllModels) {
            return this.getAllEntityModels().always(() => {
                this.actions.loadModelFinish(null);
            });
        }

        return null;
    }

    public loadModels(dataSources?: number[], reload?: boolean): JQueryPromise<IEntitites> {
        if (this._state.loadingAllModelsDeffered != null) {
            return this._state.loadingAllModelsDeffered.promise();
        }

        if (reload !== true && this._state.allEntitesAreLoaded) {
            //Do nothing.
            return $.Deferred<IEntitites>().resolve(this._state.entities).promise();
        }

        const result = this.getModels(dataSources, reload);

        if (result != null) {
            return result;
        } else if (_.isEmpty(this._state.loadingModelPromises) == false) {
            return this.waitForLoadingModelPromises();
        } else {
            //Do nothing. Demanded Entities are loaded.
            return $.Deferred<IEntitites>().resolve(this._state.entities).promise();
        }
    }

    public modelsAreLoaded(dataSourcesID?: number[]): boolean {
        if (this._state.loadingAllModelsDeffered != null) {
            return false;
        }

        if (dataSourcesID == null) {
            const dataSources = this.getDataSources();

            if (dataSources == null || dataSources.length === 0) {
                return false;
            }

            dataSourcesID = _.map(dataSources, (dataSource) => {
                return dataSource.dataSourceID;
            });
        }

        for (let i = 0; i < dataSourcesID.length; i++) {
            const dataSourceID = dataSourcesID[i];

            if (dataSourceID != null) {
                if (
                    this._state.loadingModelPromises != null &&
                    this._state.loadingModelPromises[dataSourceID] != null
                ) {
                    return false;
                }

                if (this._state.entities[dataSourceID] == null) {
                    return false;
                }
            }
        }

        return true;
    }

    public isFunction(entity: ITreeItem) {
        if (entity == null) {
            return null;
        }

        let result = entity.original != null && entity.original.IsFunc;

        if (!result && entity.parentEntity != null && entity.parentEntity.original != null) {
            result = entity.parentEntity.original.IsFunc;
        }

        return result;
    }

    public belongsToFunction(attribute: string, dataSourceID: number) {
        const entity = this.getReferredEntity(attribute, dataSourceID);

        return this.isFunction(entity);
    }

    public isFunctionParameter(attribute: string) {
        const attributes = attribute.split(ATTRIBUTE_SEPARATOR);

        return _.last(attributes)[0] === '@';
    }

    public getDataSources(): IDatasource[] {
        return this._state.dataSources;
    }

    public getDataSourceByID(id: number): IDatasource {
        return _.findWhere(this._state.dataSources, { dataSourceID: id });
    }

    public getDataSourceNameByID(id: number): string {
        const dataSource = this.getDataSourceByID(id);

        return dataSource && dataSource.name;
    }

    public getDataSourceIDByName(name: string): number {
        const dataSource = _.findWhere(this._state.dataSources, { name });

        return dataSource && dataSource.dataSourceID;
    }

    public storeDataSourceID(dataSourceID?: number): void {
        if (dataSourceID != null) {
            this.glob.AppStore.saveToLocalStorage(SELECTED_DATASOURCE_ID_KEY, dataSourceID.toString());
        } else {
            this.glob.AppStore.removeFromLocalStorage(SELECTED_DATASOURCE_ID_KEY);
        }
    }

    public getStoredDataSourceID(): number {
        const value = this.glob.AppStore.getFromLocalStorage(SELECTED_DATASOURCE_ID_KEY);

        let result: number;

        if (value != null) {
            result = parseInt(value);

            if (this._state.dataSources.length > 0 && this.getDataSourceByID(result) == null) {
                result = DEFAULT_DATASOURCE_ID;
            }
        } else {
            result = DEFAULT_DATASOURCE_ID;
        }

        return result;
    }

    public getEntitiesTreeByDS(dataSourceID: number) {
        return this._state.entitiesTree[dataSourceID];
    }

    public getEntitiesByDS(dataSourceID: number) {
        return this._state.entities[dataSourceID];
    }

    public getEntitiesHashByDS(dataSourceID: number): ITreeItemsHash {
        return this._state.entitiesHash != null ? this._state.entitiesHash[dataSourceID] : null;
    }

    public getEntityFromHash(entityName: string, dataSourceID: number): ITreeItem {
        const hash = this.getEntitiesHashByDS(dataSourceID);

        return hash != null ? hash[entityName] : null;
    }

    private loadModelLeaf(entityName: string, dataSourceID: number): boolean {
        let result = false;

        const entity = this.getEntityFromHash(entityName, dataSourceID);

        if (entity == null) {
            return result;
        }

        const children = entity.children;

        if (entity.original.type === ATTRIBUTE_TYPES.ENUMERATION && children.length === 0) {
            const hashCollection = this._state.entitiesHash[dataSourceID];

            const prefix = entityName.split(ATTRIBUTE_SEPARATOR);
            const key = entity.original.refer;

            const entities = this._state.entities[dataSourceID];
            const entititesHash = EntityModelsStore.createEntitiesHash(entities);
            const entityHash = entititesHash[key];
            const attributes = EntityModelsStore.sortEntities(entityHash.attributes);

            attributes.forEach((attribute) => {
                const attributeHash = attribute.refer != null ? entititesHash[attribute.refer] : null;
                const item = EntityModelsStore.createTreeItem(
                    attributeHash,
                    attribute,
                    prefix,
                    entityHash.keyAttribute,
                    entityHash.DefaultAttribute,
                    entity
                );

                this.setServiceProperties(attribute);

                hashCollection[item.id] = item;
                children.push(item);
                result = true;
            });
        }

        return result;
    }

    public getReferredEntity(attribute: string, dataSourceID: number): ITreeItem {
        if (attribute == null || attribute.length === 0) {
            return null;
        }

        let entity = this.getEntity(attribute, dataSourceID);

        while (true) {
            const referer = entity != null && entity.original != null ? entity.original.refer : null;

            if (referer == null || entity.id === referer || referer === attribute) {
                break;
            }

            entity = this.getEntity(referer, dataSourceID);
        }

        if (entity == null && window['jestTestRunTime_Mode'] == null) {
            if (!this.modelsAreLoaded([dataSourceID])) {
                console.log(
                    `WARNING! Fail to define reffered Entity because its Datasource ${dataSourceID} is not loaded.`
                );
            }
        }

        return entity;
    }

    public getReferredAttribute(attribute: string, dataSourceID: number): string {
        const entity = this.getReferredEntity(attribute, dataSourceID);

        if (entity != null) {
            return entity.id;
        } else {
            return null;
        }
    }

    public getDefaultParentAttribute(attribute: string, dataSourceID: number): string {
        const entity = this.getReferredEntity(attribute, dataSourceID);

        return this.getEntityDefaultParentAttribute(entity);
    }

    public getEntityDefaultParentAttribute(entity: ITreeItem): string {
        let result: string;

        if (entity != null) {
            const attributes = entity.original.attributes;

            if (attributes == null) {
                return;
            }

            for (let i = 0; i < attributes.length; i++) {
                const attribute = attributes[i];

                if (attribute.refer === entity.id) {
                    if (attribute.name.toLowerCase() === 'parent') {
                        return attribute.name;
                    } else if (result == null) {
                        result = attribute.name;
                    }
                }
            }
        }

        return result;
    }

    public getEntity(attribute: string, dataSourceID: number): ITreeItem {
        if (attribute == null || attribute.length === 0) {
            return null;
        }

        let entity = this.getEntityFromHash(attribute, dataSourceID);

        if (entity == null) {
            const attributeParts = attribute.split(ATTRIBUTE_SEPARATOR);

            if (attribute.indexOf('^') >= 0) {
                // It has a backward reference. Reduce Attribute to it.
                let modifiedAttribute: string;

                for (let i = attributeParts.length - 1; i >= 0; --i) {
                    const attributePart = attributeParts[i];

                    if (modifiedAttribute == null) {
                        modifiedAttribute = attributePart;
                    } else {
                        modifiedAttribute = attributePart + ATTRIBUTE_SEPARATOR + modifiedAttribute;
                    }

                    if (modifiedAttribute[0] === '^') {
                        modifiedAttribute = modifiedAttribute.substring(1);
                        break;
                    }
                }

                entity = this.getEntity(modifiedAttribute, dataSourceID);
            } else {
                // It might be a referrer.
                if (attributeParts.length <= 2) {
                    return null;
                }

                let parentAttribute = attributeParts.slice(0, attributeParts.length - 1).join(ATTRIBUTE_SEPARATOR);

                parentAttribute = this.getReferredAttribute(parentAttribute, dataSourceID);

                const modifiedAttribute =
                    parentAttribute + ATTRIBUTE_SEPARATOR + attributeParts[attributeParts.length - 1];

                entity = this.getEntityFromHash(modifiedAttribute, dataSourceID);
            }
        }

        return entity;
    }

    public getKeyAttribute(attribute: string, dataSourceID: number): string {
        const entity = this.getReferredEntity(attribute, dataSourceID);

        return EntityModelsStore.getEntityKeyAttribute(entity);
    }

    public static getEntityKeyAttribute(entity: ITreeItem): string {
        if (entity == null || entity.original == null) {
            return null;
        }

        const keyAttribute = entity.original.keyAttribute;

        if (keyAttribute != null && keyAttribute !== '') {
            return keyAttribute;
        } else {
            return null;
        }
    }

    public getReferedKeyAttribute(attribute: string, dataSourceID: number): string {
        const entity = this.getReferredEntity(attribute, dataSourceID);

        if (entity == null) {
            return null;
        }

        if (entity.isKeyAttribute) {
            const attributeParts = attribute.split(ATTRIBUTE_SEPARATOR);

            return attributeParts[attributeParts.length - 1];
        }

        if (entity.original != null && entity.original.type !== 'e') {
            attribute = EntityModelsStore.getAttributeEntityName(entity.name);
        }

        let result = this.getKeyAttribute(attribute, dataSourceID);

        // Attribute can have the same name as its Entity.
        if (result != null && attribute !== result) {
            const item = this.getEntityFromHash(result, dataSourceID);

            if (item != null) {
                const keyAttribute = this.getReferedKeyAttribute(result, dataSourceID);

                result += ATTRIBUTE_SEPARATOR + keyAttribute;
            }
        }

        return result;
    }

    public getReferredEntityKeyAttributeType(attribute: string, dataSourceID: number) {
        const referredEntity = this.getReferredEntity(attribute, dataSourceID);

        if (referredEntity != null) {
            const keyAttribute = this.getReferedKeyAttribute(attribute, dataSourceID);

            if (keyAttribute != null) {
                return this.getAttributeType(referredEntity.name + ATTRIBUTE_SEPARATOR + keyAttribute, dataSourceID);
            }
        }
    }

    public static getAttributeEntityName(attribute: string) {
        return attribute != null && attribute.length > 0 ? attribute.split(ATTRIBUTE_SEPARATOR)[0] : null;
    }

    public getDefaultAttribute(attribute: string, dataSourceID: number): string {
        const entity = this.getReferredEntity(attribute, dataSourceID);

        return EntityModelsStore.getEntityDefaultAttribute(entity);
    }

    public static getEntityDefaultAttribute(entity: ITreeItem): string {
        if (entity == null || entity.original == null) {
            return null;
        }

        const defaultAttribute = entity.original.DefaultAttribute;

        if (defaultAttribute != null && defaultAttribute !== '') {
            return defaultAttribute;
        } else {
            return null;
        }
    }

    public getReferredAttributeType(attribute: string, dataSourceID: number): string {
        if (attribute == null || attribute.length === 0) {
            return null;
        }

        const entity = this.getReferredEntity(attribute, dataSourceID);

        if (entity != null && entity.original != null) {
            return entity.original.type;
        } else {
            return null;
        }
    }

    public getAttributeType(value: string | IAttributeName, dataSourceID: number) {
        if (value == null) {
            return null;
        }

        if (typeof value === 'string') {
            return this.getReferredAttributeType(value, dataSourceID);
        } else {
            if (value.attribute != null) {
                return this.getReferredAttributeType(value.attribute, dataSourceID);
            } else {
                return getExpressionType(value.expression);
            }
        }
    }

    private getEntityType(entity: ITreeItem): string {
        if (entity != null && entity.original != null) {
            return entity.original.type;
        } else {
            return null;
        }
    }

    private getReferredDefaultAttributeType(attribute: string, dataSourceID: number): string {
        let entity = this.getReferredEntity(attribute, dataSourceID);

        if (entity != null && entity.original != null) {
            let result = entity.original.type;

            while (result === ATTRIBUTE_TYPES.ENUMERATION) {
                const defaultAttribute = entity.original.DefaultAttribute;

                if (defaultAttribute != null && defaultAttribute !== '') {
                    const fullDefaultAttribute = entity.id + ATTRIBUTE_SEPARATOR + defaultAttribute;

                    entity = this.getReferredEntity(fullDefaultAttribute, dataSourceID);
                    result = this.getEntityType(entity);
                } else {
                    result = null;
                }
            }

            return result;
        } else {
            return null;
        }
    }

    public getDefaultAttributeType(value: string | IAttributeName, dataSourceID: number) {
        if (value == null) {
            return null;
        }

        if (typeof value === 'string') {
            return this.getReferredDefaultAttributeType(value, dataSourceID);
        } else {
            if (value.attribute != null) {
                return this.getReferredDefaultAttributeType(value.attribute, dataSourceID);
            } else {
                return getExpressionType(value.expression);
            }
        }
    }

    public getReferredAttributeLabel(attribute: string, dataSourceID: number): string {
        if (attribute == null || attribute.length === 0) {
            return null;
        }

        // VR: const entity = this.getReferredEntity(attribute, dataSourceID);
        /* VR: IMPORTANT!  ====================================================
         in the chain like:
         [ParentAttr -> referredAttr1 -> refferedAttr2]
         Leo want to see a label of the Parent Attribute, not relation children
         */
        const entity = this.getEntity(attribute, dataSourceID);

        if (
            entity != null &&
            entity.original != null &&
            entity.original.label != null &&
            entity.original.label !== ''
        ) {
            return entity.original.label;
            //VR: if Entity Label is empty, then Leo wants to show Attribute name:
        } else {
            const arrayAttribute = attribute.split(ATTRIBUTE_SEPARATOR);

            return arrayAttribute[arrayAttribute.length - 1]; // we can add an AGGREGATE here, like 'issues.count'
        }
    }

    public getAttributeLabel(value: string | IAttributeName, dataSourceID: number, intervals: IIntervals): string {
        if (value == null) {
            return null;
        }

        let result: string;

        const attributeName = typeof value === 'string' ? value : value.attribute;

        if (attributeName != null) {
            result = this.getReferredAttributeLabel(attributeName, dataSourceID);

            if (result == null || result === '') {
                if (attributeName != null && attributeName !== '') {
                    const attributes = attributeName.split(ATTRIBUTE_SEPARATOR);

                    result = attributes[attributes.length - 1];
                }
            }
        } else if (typeof value !== 'string') {
            result = value.expression != null ? value.expression.text : null;

            if (result != null && result.length > 100) {
                result = result.slice(0, 100) + '...';
            }
        }

        if (intervals != null && intervals.timePart != null) {
            if (result != null && result.length > 0) {
                result += ' - ';
            } else {
                result = '';
            }

            result += getElementNameByType(intervals.timePart, TIME_PART_ELEMENTS.dateTimeInterval);
        }

        return result;
    }

    //LL: 15.06.18:  to use for ColorMapping
    public getOriginalReferForAttribute(attribute: string, dataSourceID: number): string {
        let entity = this.getEntity(attribute, dataSourceID);

        //LL: "entity"."DefaultAttribute"  must refer to "entity".
        //   But entity.original.refer for "entity"."DefaultAttribute" does not exist
        if (entity != null && entity.isDefaultAttribute === true) {
            if (entity.parentEntity != null) {
                //LL: 26.06.18: EU added parentEntity
                entity = entity.parentEntity;
            } else {
                //  attribute is joined by ".":  a.b.c
                var i = attribute.lastIndexOf('.');
                if (i >= 0) {
                    var attrShort = attribute.substr(0, i);
                    entity = this.getEntity(attrShort, dataSourceID);
                }
            }
        }

        if (entity != null) {
            return entity.original.refer; // null if type != 'e'
        } else {
            return null;
        }
    }

    public extendedDataAreLoaded(dataSourceID: number, entityName: string) {
        let entity = this.getEntity(entityName, dataSourceID);

        if (entity != null) {
            const entityNameParts = entity.name.split(ATTRIBUTE_SEPARATOR);

            if (entityNameParts.length > 1) {
                entity = this.getEntityFromHash(entityName, dataSourceID);
            }
        }

        return entity != null && entity.extendedProperties != null;
    }

    public loadEntityExtendedData(
        dataSourceID: number,
        entityName: string,
        reload?: boolean,
        runTimeWidgetID?: number
    ): JQuery.Promise<IEntityDataEx> {
        const result = $.Deferred<IEntityDataEx>();

        let entity = this.getEntity(entityName, dataSourceID);

        if (entity != null) {
            const entityNameParts = entity.name.split(ATTRIBUTE_SEPARATOR);

            if (entityNameParts.length > 1) {
                entityName = entityNameParts[0];
                entity = this.getEntityFromHash(entityName, dataSourceID);
            }

            if (!reload && entity != null && entity.extendedProperties) {
                return result.resolve(entity.extendedProperties);
            }
        }

        this.glob.AQLQueries.callMethod<any>(
            'attrQuery_GetEntity',
            {
                EntityName: entityName,
                dataSourceID: dataSourceID,
                wsID: DEFAULT_WORKSPACE_ID
            },
            null,
            null,
            {
                runTimeWidgetID: runTimeWidgetID
            }
        )
            .done((resp) => {
                if (entity != null) {
                    entity.extendedProperties = resp.result;
                }

                result.resolve(resp.result);
            })
            .fail(() => {
                result.reject();
            });

        return result.promise();
    }

    public loadEntitiesExtendedData(
        dataQueries: I.ILoadEntitiesExtendedDataQuery[],
        reload?: boolean,
        runTimeWidgetID?: number
    ) {
        const loadingDefs: JQuery.Promise<IEntityDataEx>[] = [];

        _.each(dataQueries, (dataQuery) => {
            const entityNames: string[] = [];

            _.each(dataQuery.entityNames, (entityName) => {
                const entityNameParts = entityName.split(ATTRIBUTE_SEPARATOR);

                entityName = entityNameParts[0];

                if (entityNames.indexOf(entityName) < 0) {
                    entityNames.push(entityName);
                }
            });

            _.each(entityNames, (entityName) => {
                const loadingDef = this.loadEntityExtendedData(
                    dataQuery.dataSourceID,
                    entityName,
                    reload,
                    runTimeWidgetID
                );

                loadingDefs.push(loadingDef);
            });
        });

        $.when(...loadingDefs).always(() => {
            this.actions.loadEntitiesExtendedDataFinish();
        });
    }

    public attributeEditingData(
        attribute: IAttributeName,
        dataSourceID: number
    ): { readOnly: boolean; jointTable?: boolean } {
        if (attribute != null && attribute.attribute != null) {
            const attributeParts = attribute.attribute.split(ATTRIBUTE_SEPARATOR);

            if (attributeParts.length <= 3) {
                const entityName = attributeParts[0];
                const entity = this.getEntityFromHash(entityName, dataSourceID);

                if (entity != null && entity.original != null) {
                    const attributeName = attributeParts[1];
                    const entityAttribute = _.find(entity.original.attributes, (attribute) => {
                        return attribute.name === attributeName;
                    });

                    if (
                        entityAttribute != null &&
                        entityAttribute.autoGen !== true &&
                        entityAttribute.Expression == null
                    ) {
                        if (attributeParts.length === 3) {
                            if (entityAttribute.isBackwardReference) {
                                const jointTableSourceEntity = this.getEntity(
                                    entityAttribute.refer + ATTRIBUTE_SEPARATOR + attributeParts[2],
                                    dataSourceID
                                );

                                if (jointTableSourceEntity != null) {
                                    const sourceEntityKeyAttribute = this.getKeyAttribute(
                                        jointTableSourceEntity.original.refer,
                                        dataSourceID
                                    );

                                    if (sourceEntityKeyAttribute != null) {
                                        const sourceEntityDefaultAttribute = this.getDefaultAttribute(
                                            jointTableSourceEntity.original.refer,
                                            dataSourceID
                                        );

                                        if (sourceEntityDefaultAttribute != null) {
                                            return { readOnly: false, jointTable: true };
                                        }
                                    }
                                }
                            }
                        } else {
                            if (entity.extendedProperties == null) {
                                return { readOnly: false };
                            }

                            const entityAttributeEx = _.find(entity.extendedProperties.Attributes, (attribute) => {
                                return attribute.AtribName === attributeName;
                            });

                            if (
                                entityAttributeEx != null &&
                                entityAttributeEx.SQLCol != null &&
                                entityAttributeEx.SQLCol !== ''
                            ) {
                                return { readOnly: false };
                            }
                        }
                    }
                }
            }
        }

        return { readOnly: true };
    }

    public isAttributeReadOnly(attribute: IAttributeName, dataSourceID: number) {
        return this.attributeEditingData(attribute, dataSourceID).readOnly;
    }

    private createEntityValueCollectionKey(
        dataSourceID: number,
        attributeData: IAttributeQueryData,
        filter: IAQLFilter[],
        sortingType: AttributeValueSortingType
    ) {
        if (dataSourceID == null || attributeData == null) {
            return undefined;
        }

        let result = 'EntityCollection-DS:' + dataSourceID;

        // Checks for Expression must go before ones for Attribute because it is hard to control that Attribute is absent when Expression is set.
        if (attributeData.expression != null) {
            result += '-E:' + (attributeData.expression.text || JSON.stringify(attributeData.expression.syntaxTree));
        } else if (attributeData.attribute != null) {
            result += '-A:' + attributeData.attribute;
        } else {
            return undefined;
        }

        if (attributeData.loadKeyAttribute) {
            const { keyAttribute } = attributeData;

            let idMark: string;

            if (keyAttribute != null) {
                if (keyAttribute.expression != null) {
                    idMark = keyAttribute.expression.text || JSON.stringify(keyAttribute.expression.syntaxTree);
                } else if (keyAttribute.attribute != null) {
                    idMark = keyAttribute.attribute;
                }
            }

            result += '-ID:' + (idMark || 'Key');
        }

        if (filter != null && filter.length > 0) {
            result += '-F:' + JSON.stringify(filter);
        }

        const { limit } = attributeData;

        if (limit != null && limit > 0) {
            result += '-L:' + limit;
        }

        if (sortingType != null) {
            result += '-S:' + sortingType;
        }

        return result;
    }

    public getEntityValueCollection(
        dataSourceID: number,
        attributeData?: IAttributeQueryData,
        filter?: IAQLFilter[],
        sortingType?: AttributeValueSortingType
    ): IEntityValueCollection {
        const collectionKey = this.createEntityValueCollectionKey(dataSourceID, attributeData, filter, sortingType);

        if (collectionKey != null) {
            return this.getCachedData<IEntityValueCollection>(collectionKey);
        }

        return undefined;
    }

    public getEntityValues(
        dataSourceID: number,
        attributeData: IAttributeQueryData,
        theme: IValuesCollection,
        filter?: IAQLFilter[]
    ): GetValuesResult {
        const result = $.Deferred<IGetValuesResult>();
        const entityCollection = this.getEntityValueCollection(dataSourceID, attributeData, filter);

        if (entityCollection != null && entityCollection.values != null && entityCollection.values.length > 0) {
            result.resolve({
                displayValues: entityCollection.displayValues
            });
        } else {
            this.loadEntityValues(dataSourceID, theme, attributeData, filter)
                .promise.done((entityCollection: IEntityValueCollection) => {
                    result.resolve({
                        displayValues: entityCollection.displayValues
                    });
                })
                .fail(() => {
                    result.reject();
                });
        }

        return result;
    }

    public entityValuesAreLoading(
        dataSourceID: number,
        attributeData?: IAttributeQueryData,
        filter?: IAQLFilter[],
        sortingType?: AttributeValueSortingType
    ): boolean {
        const collectionKey = this.createEntityValueCollectionKey(dataSourceID, attributeData, filter, sortingType);

        if (collectionKey != null) {
            return this.isCachedDataLoading(collectionKey);
        }

        return false;
    }

    public loadEntityValues(
        dataSourceID: number,
        theme: IValuesCollection,
        attributeData: IAttributeQueryData,
        filter?: IAQLFilter[],
        sortingType?: AttributeValueSortingType,
        ignoreBackendCache?: boolean,
        ignoreLocalCache?: boolean
    ): {
        promise: JQueryPromise<IEntityValueCollection>;
        dataFromCache: boolean;
        collectionKey: string;
    } {
        const runTimeWidgetID =
            attributeData.getRunTimeWidgetID != null
                ? attributeData.getRunTimeWidgetID()
                : attributeData.runTimeWidgetID;
        const collectionKey = this.createEntityValueCollectionKey(dataSourceID, attributeData, filter, sortingType);

        if (collectionKey == null) {
            this.glob.AppActions.showNotification_Fail(
                'EntityModelsStore.loadEntityValues: input data are wrong.',
                runTimeWidgetID
            );

            return {
                promise: $.Deferred().reject().promise(),
                dataFromCache: false,
                collectionKey: null
            };
        }

        const themeParameters = this.glob.AQLQueries.getAQLParametersFromTheme(theme, null, ignoreBackendCache);
        const localCacheMaxCacheAge = ignoreLocalCache ? 0 : themeParameters.maxCacheAge;

        let result = this.getCachedDataDeferred<IEntityValueCollection>(collectionKey, localCacheMaxCacheAge);

        if (result != null) {
            return {
                promise: result.promise(),
                dataFromCache: true,
                collectionKey: collectionKey
            };
        }

        result = $.Deferred<IEntityValueCollection>();

        this.putCachedDataDeferred(collectionKey, result, themeParameters.sqlTimeOut);

        this.loadModels([dataSourceID])
            .then(() => {
                const { attribute, expression, keyAttribute } = attributeData;

                let attributeRefrences: IAttributeReference[];

                // Checks for Expression must go before ones for Attribute because it is hard to control that Attribute is absent when Expression is set.
                if (expression != null) {
                    attributeRefrences = [
                        {
                            syntaxTree: expression.syntaxTree,
                            rettype: expression.rettype,
                            sort: 1,
                            sort_values: true
                        }
                    ];
                } else if (attribute != null) {
                    const attrChain = attribute.split(ATTRIBUTE_SEPARATOR);

                    attributeRefrences = [
                        {
                            attrib: attrChain,
                            sort: 1,
                            sort_values: true
                        }
                    ];

                    if (attributeData.loadKeyAttribute && keyAttribute == null) {
                        const entityKeyAttribute = this.getKeyAttribute(attribute, dataSourceID);

                        if (entityKeyAttribute != null) {
                            attributeRefrences.push({
                                attrib: attrChain.concat([entityKeyAttribute])
                            });
                        }
                    }
                } else {
                    result.reject();
                    return;
                }

                if (sortingType === 'dsc') {
                    attributeRefrences[0].sort_desc = true;
                } else if (sortingType === 'default') {
                    delete attributeRefrences[0].sort;
                }

                if (attributeData.loadKeyAttribute && keyAttribute != null) {
                    if (keyAttribute.expression != null) {
                        attributeRefrences.push({
                            syntaxTree: keyAttribute.expression.syntaxTree,
                            rettype: keyAttribute.expression.rettype
                        });
                    } else if (keyAttribute.attribute != null) {
                        attributeRefrences.push({
                            attrib: keyAttribute.attribute.split(ATTRIBUTE_SEPARATOR)
                        });
                    }
                }

                const nullHandling = new NullHandling(
                    theme,
                    null,
                    nullHandlingBehaviour.OMIT_NULLS
                ).getBackendHandling();

                let loadDataParams: IDataParameters = {
                    attrib_refs: attributeRefrences,
                    group_attribs: [],
                    dataSourceID: dataSourceID,
                    distinct: true,
                    nullHandling: nullHandling != null ? nullHandling : nullHandlingBackend.OMIT_NULLS
                };

                loadDataParams = _.extend(loadDataParams, themeParameters);

                if (filter != null) {
                    loadDataParams.filter = filter;
                }

                const { limit } = attributeData;

                if (limit != null && limit > 0) {
                    loadDataParams.limit = limit;
                }

                const aqlOptions = { ...attributeData };

                if (aqlOptions.workspace == null) {
                    aqlOptions.workspace = DEFAULT_WORKSPACE_ID;
                }

                return this.glob.AQLQueries.loadData(loadDataParams, aqlOptions);
            })
            .done((data) => {
                const values: string[] = [];
                const displayValues: IDisplayValue[] = [];

                _.each(data.result, (row) => {
                    let value = row[0];

                    if (value != null && typeof value !== 'string') {
                        value = (value as any).toString();
                    }

                    values.push(value);

                    let key: string = row.length > 1 ? row[1] : null;

                    if (key != null && typeof key !== 'string') {
                        key = (key as any).toString();
                    }

                    if (key != null) {
                        displayValues.push({
                            value: key,
                            displayValue: value
                        });
                    } else {
                        displayValues.push({
                            value: value
                        });
                    }
                });

                const dataResult: IEntityValueCollection = {
                    values: values,
                    displayValues: displayValues
                };

                this.putCachedDataValue(
                    collectionKey,
                    dataResult,
                    themeParameters.maxCacheAge,
                    themeParameters.minCacheTime
                );

                result.resolve(dataResult);
            })
            .fail(() => {
                this.cleanDataCache(collectionKey);
                result.reject();
            })
            .always(() => {
                this.actions.loadEntityValuesFinish(collectionKey);
            });

        return {
            promise: result.promise(),
            dataFromCache: false,
            collectionKey: collectionKey
        };
    }

    private createTreeEntityCollectionKey(
        dataSourceID: number,
        attribute: string,
        rootNodeID: string,
        labelAttribute: string,
        filter: IAQLFilter[],
        limit: number,
        sortingType?: AttributeValueSortingType
    ) {
        let result = 'TreeEntityCollection-DS:' + dataSourceID + '-A:' + attribute;

        if (rootNodeID != null) {
            result += '-RA:' + rootNodeID;
        }

        if (labelAttribute != null) {
            result += '-LA:' + labelAttribute;
        }

        if (filter != null && filter.length > 0) {
            result += '-F:' + JSON.stringify(filter);
        }

        if (limit != null && limit > 0) {
            result += '-L:' + limit;
        }

        if (sortingType != null) {
            result += '-S:' + sortingType;
        }

        return result;
    }

    public getTreeEntityValueCollection(
        dataSourceID: number,
        attribute: string,
        rootNodeID?: string,
        labelAttribute?: string,
        filter?: IAQLFilter[],
        limit?: number,
        sortingType?: AttributeValueSortingType
    ): IEntityValuesTree {
        if (dataSourceID != null && attribute != null) {
            const key = this.createTreeEntityCollectionKey(
                dataSourceID,
                attribute,
                rootNodeID,
                labelAttribute,
                filter,
                limit,
                sortingType
            );

            return this.getCachedData<IEntityValuesTree>(key);
        }

        return undefined;
    }

    public loadTreeEntityValues(
        dataSourceID: number,
        attribute: string,
        parentAttribute: string,
        options: IAQLOptions,
        theme: IValuesCollection,
        rootNodeID?: string,
        labelAttribute?: string,
        filter?: IAQLFilter[],
        limit?: number,
        sortingType?: AttributeValueSortingType,
        ignoreBackendCache?: boolean,
        ingnoreMaxNumberOfRecords?: boolean
    ): JQueryDeferred<IEntityValuesTree> {
        const themeParameters = this.glob.AQLQueries.getAQLParametersFromTheme(theme, null, ignoreBackendCache);
        const collectionKey = this.createTreeEntityCollectionKey(
            dataSourceID,
            attribute,
            rootNodeID,
            labelAttribute,
            filter,
            limit,
            sortingType
        );

        let result = this.getCachedDataDeferred<IEntityValuesTree>(collectionKey, themeParameters.maxCacheAge);

        if (result != null) {
            return result;
        }

        result = $.Deferred<IEntityValuesTree>();

        const fullParentAttribute =
            parentAttribute != null && parentAttribute.length > 0
                ? attribute + ATTRIBUTE_SEPARATOR + parentAttribute
                : null;
        const fullLabelAttribute =
            labelAttribute != null && labelAttribute.length > 0
                ? attribute + ATTRIBUTE_SEPARATOR + labelAttribute
                : null;

        this.putCachedDataDeferred(collectionKey, result, themeParameters.sqlTimeOut);

        this.loadModels([dataSourceID])
            .done(() => {
                let attrChain: string[];

                if (attribute != null && attribute.length > 0) {
                    attrChain = attribute.split(ATTRIBUTE_SEPARATOR);
                } else {
                    attrChain = [attribute];
                }

                const attributeRefrences: IAttributeReference[] = [
                    {
                        attrib: fullLabelAttribute != null ? fullLabelAttribute.split(ATTRIBUTE_SEPARATOR) : attrChain,
                        sort: 1,
                        sort_values: true
                    }
                ];

                if (sortingType === 'dsc') {
                    attributeRefrences[0].sort_desc = true;
                }

                const keyAttribute = this.getKeyAttribute(attribute, dataSourceID);

                if (keyAttribute != null) {
                    attributeRefrences.push({
                        attrib: attrChain.concat([keyAttribute])
                    });

                    if (fullParentAttribute != null) {
                        const attribArray = fullParentAttribute.split(ATTRIBUTE_SEPARATOR);
                        const parentEntity = this.getEntity(fullParentAttribute, dataSourceID);

                        if (parentEntity != null) {
                            const parentAttribObj: IAttributeReference = {
                                attrib: attribArray
                            };

                            if (parentEntity.original.refer != null) {
                                // Is used to get value of Key Attribute always, because getting data for Entity returns Default Attribute.
                                parentAttribObj.lookupType = 'getkey';
                            }

                            attributeRefrences.push(parentAttribObj);
                        }
                    }
                }

                let loadDataParams: IDataParameters = {
                    attrib_refs: attributeRefrences,
                    group_attribs: [],
                    distinct: true,
                    nullHandling: nullHandlingBackend.RETURN_NULLS,
                    dataSourceID: dataSourceID,
                    filter: filter
                };

                loadDataParams = _.extend(loadDataParams, themeParameters);

                if (ingnoreMaxNumberOfRecords == true) {
                    loadDataParams.maxNumberOfRecords = 0;
                }

                if (limit != null && limit > 0) {
                    loadDataParams.limit = limit;
                }

                const aqlOptions = options ?? {};

                if (aqlOptions.workspace == null) {
                    aqlOptions.workspace = DEFAULT_WORKSPACE_ID;
                }

                // 'distinct' can be set true here because it queries for ID, Parent, and Title of tree nodes. Otherwise it is wrong.
                this.glob.AQLQueries.loadData(loadDataParams, aqlOptions)
                    .done((data: ILoadDataResult) => {
                        const treeValues: ITreeNodesCollection = {}; // Does not keep values order.
                        const rootNodeIDStr = rootNodeID != null ? rootNodeID.toString() : ''; // rootNodeID might be not string.
                        const valuesArray: ITreeNode[] = []; // Keeps values order.
                        const topNodes: ITreeNode[] = [];

                        data.result?.forEach((row, index) => {
                            let id = row[1];

                            if (id != null) {
                                const title = row[0];

                                id = id.toString();

                                let parentID = null;

                                if (fullParentAttribute != null && id !== rootNodeIDStr) {
                                    parentID = row[2];
                                    parentID = parentID != null ? parentID.toString() : null;
                                }

                                if (parentID !== id) {
                                    const key = (parentID != null ? parentID + '/' : '') + id + '_' + index;
                                    const value: ITreeNode = {
                                        key: key,
                                        id: id,
                                        parentId: parentID,
                                        title: title
                                    };

                                    treeValues[id] = value;
                                    valuesArray.push(value);
                                } else {
                                    console.log(
                                        `WARNING: Wrong tree data! Node { ID:'${id}', Title:'${title}' } has parentID = ID. THE NODE IS IGNORED. Datasource: ${dataSourceID}, Attribute: ${attribute}`
                                    );
                                }
                            }
                        });

                        valuesArray.forEach((node) => {
                            const parentID = node.parentId;

                            let parentNode: ITreeNode = null;

                            if (parentID != null) {
                                if (node.id !== parentID) {
                                    parentNode = treeValues[parentID];

                                    if (parentNode != null) {
                                        if (parentNode.subNodes == null) {
                                            parentNode.subNodes = [node];
                                        } else {
                                            parentNode.subNodes.push(node);
                                        }
                                    }
                                }
                            }

                            if (parentNode == null && (rootNodeID == null || node.id === rootNodeIDStr)) {
                                topNodes.push(node);
                            }
                        });

                        const dataResult: IEntityValuesTree = {
                            nodes: treeValues,
                            tree: topNodes,
                            parentAttribute: parentAttribute,
                            rootNodeID: rootNodeID
                        };

                        this.putCachedDataValue(
                            collectionKey,
                            dataResult,
                            themeParameters.maxCacheAge,
                            themeParameters.minCacheTime
                        );

                        result.resolve(dataResult);
                    })
                    .fail(() => {
                        result.reject();
                    });
            })
            .fail(() => {
                result.reject();
            });

        result
            .fail(() => {
                this.cleanDataCache(collectionKey);
            })
            .always(() => {
                this.actions.loadTreeEntityValuesFinish(collectionKey);
            });

        return result;
    }

    public getValidEntitiesForMultipeRootFunctionality(
        attrQuery: { attribute?: string | IExpression }[] | { attribute?: string | IExpression },
        attrQueryFilter: { attribute?: string }[]
    ): string[] {
        const result: string[] = [];

        const addFirstEntity = (attribute: string | IExpression) => {
            if (typeof attribute === 'string') {
                const visibleEntity = attribute.split(ATTRIBUTE_SEPARATOR)[0];

                if (result.indexOf(visibleEntity) < 0) {
                    result.push(visibleEntity);
                }
            }
        };

        if (Array.isArray(attrQuery)) {
            _.each(attrQuery, (query) => {
                addFirstEntity(query.attribute);
            });
        } else {
            addFirstEntity(attrQuery.attribute);
        }

        _.each(attrQueryFilter, (filter) => {
            addFirstEntity(filter.attribute);
        });

        return result;
    }

    private findEntityByName(entities: IDataLoadEntity[], name: string) {
        for (const entity of entities) {
            if (entity.name === name) {
                return entity;
            }
        }

        return null;
    }

    public reduceAttributeToClosestEntity(attributeName: string, dataSourceID: number): string {
        let result = attributeName;

        const attributeParts = attributeName.split(ATTRIBUTE_SEPARATOR);
        const numberOfAttributes = attributeParts.length;
        const entities = this._state.entities[dataSourceID];

        if (numberOfAttributes > 1 && entities != null && entities.length > 0) {
            let entityNumber = 0;
            let entity: IDataLoadEntity;
            let newAttribute: string;

            for (let attributeNumber = numberOfAttributes - 1; attributeNumber >= 0; attributeNumber--) {
                const attributePart = attributeParts[attributeNumber];

                entity = this.findEntityByName(entities, attributePart);

                if (entity != null) {
                    //Entity is found. Stop here.
                    newAttribute = entity.name;
                    entityNumber = attributeNumber;
                    break;
                }
            }

            if (entityNumber < numberOfAttributes) {
                //Entity is not the last Attribute. Check for referrer.
                const numberOfEntityAttributes = entity.attributes.length;
                const tmpAttribute = attributeParts[entityNumber + 1];

                for (let attrNumber = 0; attrNumber < numberOfEntityAttributes; attrNumber++) {
                    let attribute = entity.attributes[attrNumber];

                    if (attribute.name === tmpAttribute) {
                        if (attribute.type === ATTRIBUTE_TYPES.ENUMERATION) {
                            //It's referrer.
                            newAttribute = attribute.refer;
                            entityNumber++;
                        }
                        break;
                    }
                }
            }

            for (let attrNumber = entityNumber + 1; attrNumber < numberOfAttributes; attrNumber++) {
                newAttribute += ATTRIBUTE_SEPARATOR + attributeParts[attrNumber];
            }

            result = newAttribute;
        }

        return result;
    }

    // public static findTreeNodeByID(nodes: ITreeValuesCollection | ITreeValue[], id: string): ITreeValue {
    //     if (nodes != null) {
    //         for (let nodeKey in nodes) {
    //             const node = nodes[nodeKey];

    //             if (node.id === id) {
    //                 return node;
    //             }
    //             else {
    //                 const res = EntityModelsStore.findTreeNodeByID(node.subNodes, id);
    //                 if (res != null) {
    //                     return res;
    //                 }
    //             }
    //         }
    //     }
    //     return;
    // }

    // public static getTreeBranch(entityValuesTree: IEntityValuesTree, branchRootID: number | string): string[] {
    //     let result = [];

    //     const valueID = branchRootID.toString();
    //     result.push(valueID);

    //     if (entityValuesTree != null) {
    //         const treeValues = entityValuesTree.values;
    //         const branchRootNode = EntityModelsStore.findTreeNodeByID(treeValues, valueID);

    //         if (branchRootNode != null) {
    //             EntityModelsStore.addSubValues(branchRootNode.subNodes, result);
    //         }
    //     }

    //     return result;
    // }

    // private static addSubValues(subNodes: ITreeValue[], selectedValues: string[]) {
    //     if (subNodes != null && subNodes.length > 0) {
    //         _.each(subNodes, (node) => {
    //             if (node.id != null) {
    //                 selectedValues.push(node.id);

    //                 EntityModelsStore.addSubValues(node.subNodes, selectedValues);
    //             }
    //         });
    //     }
    // }

    // public isDefaultAttribute(attribute: string, dataSourceID: number): boolean {
    //     const entity = this.getReferredEntity(attribute, dataSourceID);
    //     if (entity != null) {
    //         const entityPartPath = Store.getEntityPartPath(entity.id);
    //         if (entityPartPath !== attribute) {
    //             const entityPartEntity = this._state.dsEntitiesHash[dataSourceID][entityPartPath];
    //             const defaultAttribute = entityPartEntity.original.DefaultAttribute;

    //             return entityPartPath + ATTRIBUTE_SEPARATOR + defaultAttribute === attribute;
    //         }
    //     }
    //     return false;
    // }

    public useLoadDataSourcesHook(reload?: boolean) {
        const [loaded, setLoaded] = useState(!reload && this._state.dataSourcesAreLoaded);

        useEffect(() => {
            const handleOnEntityModelsChange = (eventType: DataloadEventTypes) => {
                if (eventType === DataloadEventTypes.DATASOURCES) {
                    setLoaded(true);
                }
            };

            if (!loaded || reload) {
                if (loaded) {
                    setLoaded(false);
                }

                this.actions.loadDataSources(reload);
            }

            this.addOnDataLoadListener(handleOnEntityModelsChange);

            return () => {
                this.removeOnDataLoadListener(handleOnEntityModelsChange);
            };
        }, [reload]);

        return loaded;
    }

    public useLoadModelsHook(datasources?: number[], reload?: boolean) {
        const [loaded, setLoaded] = useState(!reload && this.modelsAreLoaded(datasources));

        useEffect(() => {
            const handleOnEntityModelsChange = (eventType: DataloadEventTypes) => {
                if (eventType === DataloadEventTypes.MODEL) {
                    if (this.modelsAreLoaded(datasources)) {
                        setLoaded(true);
                    }
                }
            };

            if (!loaded || reload || !this.modelsAreLoaded(datasources)) {
                if (loaded) {
                    setLoaded(false);
                }

                this.glob.EntityModelsActions.loadModels(datasources, reload);
            }

            this.addOnDataLoadListener(handleOnEntityModelsChange);

            return () => {
                this.removeOnDataLoadListener(handleOnEntityModelsChange);
            };
        }, [reload, JSON.stringify(datasources)]);

        return loaded;
    }

    public addOnDataLoadListener(callback: (eventType: DataloadEventTypes) => void) {
        this.on(DATA_LOADED_EVENT, callback);
    }

    public removeOnDataLoadListener(callback: (eventType: DataloadEventTypes) => void) {
        this.off(DATA_LOADED_EVENT, callback);
    }

    public addOnModelsLoadListener(callback: (loadedDataSources: number[]) => void) {
        this.on(MODELS_LOADED_EVENT, callback);
    }

    public removeOnModelsLoadListener(callback: (loadedDataSources: number[]) => void) {
        this.off(MODELS_LOADED_EVENT, callback);
    }

    public addOnCacheClearedListener(callback: (eventType: DataloadEventTypes) => void) {
        this.on(CACHE_CLEARED_EVENT, callback);
    }

    public removeOnCacheClearedListener(callback: (eventType: DataloadEventTypes) => void) {
        this.off(CACHE_CLEARED_EVENT, callback);
    }

    protected eventsHandler(payload) {
        if (!super.eventsHandler(payload)) {
            return false;
        }

        const action = payload.action;

        if (checkAction<I.ILoadModels>(action, ActionConstants.LOAD_MODELS)) {
            if (
                this._state.loadingAllModelsDeffered == null &&
                (action.reload === true || !this._state.allEntitesAreLoaded)
            ) {
                if (this.getModels(action.dataSources, action.reload) != null) {
                    this.emitChangeWithoutSaving();
                }
            }
        } else if (checkAction<I.ILoadModelsFinish>(action, ActionConstants.LOAD_MODELS_FINISH)) {
            this.trigger(MODELS_LOADED_EVENT, action.dataSources);
            this.trigger(DATA_LOADED_EVENT, DataloadEventTypes.MODEL);
            this.emitChange();
        } else if (checkAction<I.ILoadDataSources>(action, ActionConstants.LOAD_DATA_SOURCES)) {
            if (
                this._state.loadingDataSourcesDeffered == null &&
                (action.reload === true || this._state.dataSources.length === 0)
            ) {
                this.loadDataSources(action.reload);
                this.emitChangeWithoutSaving();
            }
        } else if (checkAction<I.IAction>(action, ActionConstants.LOAD_DATA_SOURCES_FINISH)) {
            this.trigger(DATA_LOADED_EVENT, DataloadEventTypes.DATASOURCES);
            this.emitChange();
        } else if (checkAction<I.ILoadEntityValues>(action, ActionConstants.LOAD_ENTITY_VALUES)) {
            this.loadEntityValues(action.dataSourceID, action.theme, action.attributeData, action.filter);
            this.emitChangeWithoutSaving();
        } else if (checkAction<I.IAction>(action, ActionConstants.LOAD_ENTITY_VALUES_FINISH)) {
            this.trigger(DATA_LOADED_EVENT, DataloadEventTypes.ENTITY_VALUES);
            this.emitChangeWithoutSaving();
        } else if (checkAction<I.ILoadEntitiesExtendedData>(action, ActionConstants.LOAD_ENTITIES_EXTENDED_DATA)) {
            this.loadEntitiesExtendedData(action.dataQueries, action.refresh, action.runTimeWidgetID);
        } else if (checkAction<I.IAction>(action, ActionConstants.LOAD_ENTITIES_EXTENDED_DATA_FINISH)) {
            this.trigger(DATA_LOADED_EVENT, DataloadEventTypes.ENTITY_EXTENDED_DATA);
            this.emitChangeWithoutSaving();
        } else if (checkAction<I.ILoadTreeEntityValues>(action, ActionConstants.LOAD_TREE_ENTITY_VALUES)) {
            this.loadTreeEntityValues(
                action.dataSourceID,
                action.attribute,
                action.parentAttribute,
                action.options,
                action.theme,
                action.rootNodeID,
                action.labelAttribute,
                action.filter,
                action.limit,
                action.sortingType,
                action.ignoreCache
            );
            this.emitChangeWithoutSaving();
        } else if (checkAction<I.IAction>(action, ActionConstants.LOAD_TREE_ENTITY_VALUES_FINISH)) {
            this.trigger(DATA_LOADED_EVENT, DataloadEventTypes.TREE_ENTITY_VALUES);
            this.emitChangeWithoutSaving();
        } else if (checkAction<I.IAction>(action, ActionConstants.CLEAN_CACHE)) {
            this.cleanCache();
            this.trigger(CACHE_CLEARED_EVENT, DataloadEventTypes.CACHING);
        } else if (checkAction<I.ILoadModelLeafOnDemand>(action, ActionConstants.LOAD_MODEL_LEAF_ON_DEMAND)) {
            if (this.loadModelLeaf(action.entityName, action.dataSourceID)) {
                this.trigger(DATA_LOADED_EVENT, DataloadEventTypes.MODEL_LEAF);
                this.emitChange();
            }
        }

        return true;
    }

    static get(glob: IGlob, dispatcher: EntityModelsDispatcher, actions: EntityModelsActions) {
        return new EntityModelsStore(glob, dispatcher, actions);
    }

    public static init(glob: IGlob) {
        if (glob.EntityModelsDispatcher == null) {
            glob.EntityModelsDispatcher = EntityModelsDispatcher.get();
        }

        if (glob.EntityModelsActions == null) {
            glob.EntityModelsActions = EntityModelsActions.get(glob.EntityModelsDispatcher);
        }

        if (glob.EntityModelsStore == null) {
            glob.EntityModelsStore = EntityModelsStore.get(glob, glob.EntityModelsDispatcher, glob.EntityModelsActions);
        }

        // Obsolete.
        if (glob.AttrQueryStore == null) {
            glob.AttrQueryStore = glob.EntityModelsStore;
        }
    }
}

//EVG 14.03.25: The frontend has no special operation for bigint. It's in the backend only.
// So, I've decided to consider bigint as integer by default.
// If another operation is needed, originalType is set to bigint.
export function getExpressionType(expression: IExpression) {
    if (expression == null) {
        return null;
    }

    let result = expression.rettype;

    if (result === ATTRIBUTE_TYPES.BIGINT) {
        result = ATTRIBUTE_TYPES.INTEGER;
    }

    return result;
}

// Attribute can be a reference to another attribute.
// In such case 'type' of the attribute must be === 'e' (is used for widgets).
// This function is created support using of 'e' for coding purposes.
function setTypeForAttributes(entities: IDataLoadEntity[]) {
    entities?.forEach((entity) => {
        entity?.attributes?.forEach((attribute) => {
            if (attribute.refer != null) {
                attribute.type = ATTRIBUTE_TYPES.ENUMERATION;
            } else if (attribute.type === ATTRIBUTE_TYPES.BIGINT) {
                //EVG 14.03.25: The frontend has no special operation for bigint. It's in the backend only.
                // So, I've decided to consider bigint as integer by default.
                // If another operation is needed, originalType is set to bigint.
                attribute.type = ATTRIBUTE_TYPES.INTEGER;
                attribute.originalType = ATTRIBUTE_TYPES.BIGINT;
            }
        });
    });
}
